import { Button } from "@faire/slate/components/button";
import type { SVGIconProps } from "@faire/slate-icons/types";
import React from "react";

import { cn } from "../../lib/utils";

/**
 * Compare products section from the retailer PDP (Compass).
 *
 * An AI-curated comparison: the retailer's current product (the "anchor" card)
 * sits alongside up to three "aspect" cards, each surfacing one dimension where
 * an alternative wins for this store (lowest order minimum, shortest lead time,
 * highest ratings, …). Each card carries a short rationale and a "Tell me more"
 * CTA that (in production) opens the assistant.
 */

export type ComparisonAspectIcon = "orderMinimum" | "leadTime" | "rating";

export interface ComparisonCard {
  image: string;
  price: string;
  msrp: string;
  name: string;
  brand: string;
  /** Bullet rationale; nodes may include <strong> for emphasis. */
  rationale: React.ReactNode[];
}

export interface ComparisonAspectCard extends ComparisonCard {
  /** Badge label, e.g. "Lowest order minimum". */
  aspectLabel: string;
  aspectIcon: ComparisonAspectIcon;
}

export interface CompareProductsProps {
  /** Name woven into the subheading ("…stacks up against others for X."). */
  retailerName?: string;
  anchor?: ComparisonCard;
  aspects?: ComparisonAspectCard[];
  onTellMeMore?: (card: ComparisonCard) => void;
}

const DEFAULT_ANCHOR: ComparisonCard = {
  image:
    "https://cdn.faire.com/fastly/a77ad980e9b7b4a54bfa0fa82eafdd5b87364db7e49caeb2955345f1bc10d14c.jpeg",
  price: "$16.00",
  msrp: "MSRP $32.00",
  name: "Orange Petal Marshmallow | Best Seller | 8 oz Candle",
  brand: "Kandlery",
  rationale: [
    <>
      <strong>50% margin</strong> on unique Asian-inspired scents
    </>,
    <>
      Handmade <strong>women-owned</strong> quality from a Top Shop brand
    </>,
  ],
};

const DEFAULT_ASPECTS: ComparisonAspectCard[] = [
  {
    aspectLabel: "Lowest order minimum",
    aspectIcon: "orderMinimum",
    image:
      "https://cdn.faire.com/fastly/68edf5c6710ec744bcbcca1933963da5c4711af843c91882332652f18135f594.jpeg",
    price: "$11.99",
    msrp: "MSRP $23.98",
    name: "Jasmine & Orange Blossom - Medium Glass - Just Bee Candle",
    brand: "Just Bee",
    rationale: [
      <>
        Low <strong>$0</strong> first-order minimum — easy to test with no risk
      </>,
      <>
        Compared to your current product&rsquo;s <strong>$75</strong> minimum
      </>,
    ],
  },
  {
    aspectLabel: "Shortest lead time",
    aspectIcon: "leadTime",
    image:
      "https://cdn.faire.com/fastly/36fbce3cab7ca064deb0163d1a5f77f4351936e6681c92b6ce54939178035e7c.jpeg",
    price: "$11.00",
    msrp: "MSRP $22.00",
    name: "Sunburn 4 oz Candle",
    brand: "The Sensory Riot",
    rationale: [
      <>
        Fast <strong>0–2 day</strong> shipping — perfect for quick gift
        restocking
      </>,
      <>
        Compared to your current product&rsquo;s <strong>8–10 day</strong> window
      </>,
    ],
  },
  {
    aspectLabel: "Highest ratings",
    aspectIcon: "rating",
    image:
      "https://cdn.faire.com/fastly/6098f724a46b4db8077b801dd5fa498932a0077ad120ffbf8d7bf4a9e8181dfe.jpeg",
    price: "$20.00",
    msrp: "MSRP $40.00",
    name: "Orange Blossom Candle",
    brand: "Putnam Design LLC",
    rationale: [
      <>
        Exceptional <strong>4.9 rating</strong> from <strong>561</strong> happy
        retailers
      </>,
      <>
        Your current product has <strong>no customer reviews</strong> yet
      </>,
    ],
  },
];

const CompareProducts: React.FC<CompareProductsProps> = ({
  retailerName = "your store",
  anchor = DEFAULT_ANCHOR,
  aspects = DEFAULT_ASPECTS,
  onTellMeMore,
}) => {
  return (
    <div
      className="flex flex-col gap-(--slate-dimensions-6)"
      data-test-id="product-comparison-section"
    >
      <div className="flex flex-col gap-(--slate-dimensions-1)">
        <div className="text-fs-text-primary flex items-center gap-(--slate-dimensions-2)">
          <h4 className="text-fs-text-primary m-0 font-[family-name:var(--slate-typography-family-serif)] text-[length:var(--slate-typography-font-size-400)] leading-[var(--slate-typography-line-height-400)] font-[var(--slate-typography-weight-regular)]">
            Compare products
          </h4>
          <InfoIcon />
        </div>
        <p className="type-fs-paragraph text-fs-text-primary m-0">
          See how this product stacks up against others for{" "}
          <span className="type-fs-paragraph-medium">{retailerName}</span>.
        </p>
      </div>

      <div className="bg-fs-expressive-surface-neutral @container rounded-(--slate-dimensions-2) p-(--slate-dimensions-6)">
        <div className="grid grid-cols-2 gap-(--slate-dimensions-4) @[680px]:grid-cols-4">
          <ComparisonCardView
            card={anchor}
            badge="current"
            onTellMeMore={onTellMeMore}
          />
          {aspects.map((aspect) => (
            <ComparisonCardView
              key={aspect.name}
              card={aspect}
              badge="aspect"
              aspect={aspect}
              onTellMeMore={onTellMeMore}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompareProducts;

interface ComparisonCardViewProps {
  card: ComparisonCard;
  badge: "current" | "aspect";
  aspect?: ComparisonAspectCard;
  onTellMeMore?: (card: ComparisonCard) => void;
}

const ComparisonCardView: React.FC<ComparisonCardViewProps> = ({
  card,
  badge,
  aspect,
  onTellMeMore,
}) => {
  return (
    <div className="@container insitu-card bg-fs-surface-primary border-fs-border-muted flex flex-col overflow-hidden rounded-(--slate-radius-component-default) border">
      <div className="relative aspect-[4/3] overflow-hidden @[210px]:aspect-square">
        <img
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          src={card.image}
        />
        {badge === "current" ? (
          <span className="border-fs-border-muted/70 bg-fs-surface-primary type-fs-label-medium text-fs-text-primary absolute bottom-(--slate-dimensions-3) left-(--slate-dimensions-3) inline-flex min-h-[22px] items-center rounded-(--slate-radius-component-default) border border-solid px-[7px] py-[3px]">
            Current
          </span>
        ) : null}
        {badge === "aspect" && aspect ? (
          <div className="border-fs-border-muted/70 bg-fs-surface-primary/85 absolute top-(--slate-dimensions-3) left-(--slate-dimensions-3) inline-flex items-center justify-center gap-2 rounded-(--slate-radius-component-default) border border-solid px-3 py-1">
            <span className="hidden @[210px]:inline-flex">
              <AspectIcon icon={aspect.aspectIcon} />
            </span>
            <span className="type-fs-label-medium text-fs-text-primary @[210px]:type-fs-paragraph-medium whitespace-nowrap">
              {aspect.aspectLabel}
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-(--slate-dimensions-2) p-(--slate-dimensions-3) @[210px]:gap-(--slate-dimensions-3)">
        <div className="flex flex-col gap-0.5">
          <div className="flex flex-row flex-wrap items-baseline gap-x-1 leading-[0]">
            <span className="type-fs-subheading-medium text-fs-text-primary">
              {card.price}
            </span>
            <span className="type-fs-label text-fs-text-primary">
              {card.msrp}
            </span>
          </div>
          <p className="type-fs-paragraph-medium text-fs-text-primary m-0 line-clamp-2 break-words">
            {card.name}
          </p>
          <span className="type-fs-paragraph text-fs-text-primary block w-fit max-w-full truncate underline underline-offset-2">
            {card.brand}
          </span>
        </div>

        <ul className="type-fs-paragraph text-fs-text-primary m-0 hidden list-outside list-disc pl-6 @[210px]:block [&_strong]:font-semibold">
          {card.rationale.map((item, index) => (
            <li key={index} className="py-1">
              {item}
            </li>
          ))}
        </ul>

        <div className="mt-auto">
          <Button
            variant="secondary"
            size="xSmall"
            iconProps={{ Component: ChatIcon, position: "start" }}
            onClick={() => onTellMeMore?.(card)}
          >
            Tell me more
          </Button>
        </div>
      </div>
    </div>
  );
};

const AspectIcon: React.FC<{ icon: ComparisonAspectIcon }> = ({ icon }) => {
  // Inline SVGs use a literal orange hex — var() tokens do not resolve inside
  // an SVG fill attribute.
  const fill = "#e65126";
  if (icon === "orderMinimum") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill={fill}
        aria-hidden="true"
        className="shrink-0"
      >
        <path d="M20 2H4c-1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-1-2-2-2zm-5 12H9c-.55 0-1-.45-1-1s.45-1 1-1h6c.55 0 1 .45 1 1s-.45 1-1 1zm5-7H4V4l16-.02V7z" />
      </svg>
    );
  }
  if (icon === "leadTime") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill={fill}
        aria-hidden="true"
        className="shrink-0"
      >
        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={fill}
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  );
};

const InfoIcon: React.FC = () => (
  <svg
    className="size-4 shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    aria-label="Learn more about how compare products works"
    role="img"
  >
    <path
      d="M.857 12A11.14 11.14 0 0 0 12 23.143 11.14 11.14 0 0 0 23.143 12 11.14 11.14 0 0 0 12 .857 11.14 11.14 0 0 0 .857 12Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 12v6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.14 7.71a.86.86 0 1 0 1.72 0 .86.86 0 0 0-1.72 0Z"
      fill="currentColor"
    />
  </svg>
);

const ChatIcon: React.FC<SVGIconProps> = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M11.997.923A11.07 11.07 0 0 0 2.239 6.77a11.08 11.08 0 0 0 .541 11.365L.923 23.077l6.218-1.125a11.072 11.072 0 0 0 15.652-7.46A11.08 11.08 0 0 0 16.81 2.022 11.1 11.1 0 0 0 11.997.923Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
